<template>
  <div>
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title>
        <router-link class="text-decoration--none" :to="{ name: 'users' }"
          >Users</router-link
        >
        <v-icon> {{ icons.arrow }} </v-icon>
        <span v-if="form">{{ form.first_name }} {{ form.last_name }}</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>
    <div class="ma-4" v-if="form">
      <div class="display-2 font-weight-light mb-10">
        {{ form.first_name }} {{ form.last_name }}
      </div>
      <div class="d-flex flex-row align-stretch mb-12">
        <div>
          <v-avatar size="120" ref="userAvatar">
            <img :src="getUserAvatar" alt="avatar" />
          </v-avatar>
        </div>
        <div class="d-flex flex-column justify-space-around ml-4">
          <v-btn
            text
            color="primary"
            @click="showCropperModal = !showCropperModal"
            :loading="avatarLoading"
          >
            <v-icon left>{{ icons.addImage }}</v-icon>
            Change Image
          </v-btn>
          <image-cropper v-model="showCropperModal" @change="avatarChange" />
        </div>
      </div>
      <v-form ref="form" v-if="form">
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              required
              v-model="form.first_name"
              label="First name"
              :error-messages="form.$getError('first_name')"
            ></v-text-field>

            <v-text-field
              required
              outlined
              v-model="form.last_name"
              label="Last name"
              :error-messages="form.$getError('last_name')"
            ></v-text-field>

            <v-text-field
              readonly
              outlined
              required
              v-model="form.email"
              label="E-mail"
              :error-messages="form.$getError('email')"
            >
              <v-tooltip slot="append" top v-if="user.email">
                <template v-slot:activator="{ on }">
                  <v-icon color="success" v-on="on" v-if="user.email_verified">
                    {{ icons.check }}
                  </v-icon>
                  <v-icon color="warning" v-on="on" v-else>
                    {{ icons.alert }}
                  </v-icon>
                </template>
                <span v-if="user.email_verified">Email Verified</span>
                <span v-else>Unverified email</span>
              </v-tooltip>
            </v-text-field>

            <v-text-field
              readonly
              outlined
              required
              v-model="form.phone_number"
              label="Phone number"
              :error-messages="form.$getError('phone_number')"
            >
              <v-tooltip slot="append" top v-if="user.phone_number">
                <template v-slot:activator="{ on }">
                  <v-icon
                    color="success"
                    v-on="on"
                    v-if="user.phone_number_verified"
                  >
                    {{ icons.check }}
                  </v-icon>
                  <v-icon color="warning" v-on="on" v-else>
                    {{ icons.alert }}
                  </v-icon>
                </template>
                <span v-if="user.email_verified">Phone Number Verified</span>
                <span v-else>Unverified Phone Number</span>
              </v-tooltip>
            </v-text-field>

            <v-text-field
              outlined
              v-model="form.address"
              label="Address"
              :error-messages="form.$getError('address')"
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              outlined
              v-model="form.group"
              label="Church / Youth Group (optional)"
              :error-messages="form.$getError('group')"
            >
            </v-text-field>
            <v-text-field
              outlined
              required
              v-model="form.school"
              label="School"
              :error-messages="form.$getError('school')"
            >
            </v-text-field>
            <v-text-field
              outlined
              v-model="form.year_level"
              label="Year level"
              :error-messages="form.$getError('year_level')"
            >
            </v-text-field>

            <v-checkbox
              v-model="form.part_in_ltg"
              label="Currently part of a Christian Lunchtime Group at school"
              :error-messages="form.$getError('part_in_ltg')"
            >
            </v-checkbox>
          </v-col>
        </v-row>

        <template v-if="form.referee">
          <p class="subtitle-1">
            Referee (Parent/Youth Pastor/School Chaplain/Group Leader)
          </p>
          <v-layout row wrap>
            <v-flex class="px-2" md6 xs12>
              <v-text-field
                outlined
                required
                v-model="form.referee.name"
                label="Full name"
                :error-messages="form.$getError('referee.full_name')"
              ></v-text-field>
            </v-flex>
            <v-flex class="px-2" md6 xs12>
              <v-text-field
                outlined
                required
                v-model="form.referee.relationship"
                label="Relationship"
                :error-messages="form.$getError('referee.relationship')"
              ></v-text-field>
            </v-flex>
            <v-flex class="px-2" md6 xs12>
              <v-text-field
                outlined
                required
                v-model="form.referee.contact"
                label="Contact"
                :error-messages="form.$getError('referee.contact')"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </template>

        <v-btn
          color="primary"
          class="mr-4"
          @click="updateUserDetails"
          :loading="form.$busy"
        >
          Update
        </v-btn>
      </v-form>
    </div>
    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right>
      {{ snackbar.message }}
      <v-btn color="pink" text @click="snackbar.show = false">
        Ok
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  mdiChevronRight,
  mdiImagePlus,
  mdiDelete,
  mdiCheck,
  mdiAlertCircleOutline
} from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import Form from '@/utils/form'
import ImageCropper from '@/components/modals/ImageCropper'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'

export default {
  name: 'UserDetails',

  components: {
    ImageCropper,
    AppBarNavIcon
  },

  data() {
    return {
      icons: {
        arrow: mdiChevronRight,
        addImage: mdiImagePlus,
        delete: mdiDelete,
        check: mdiCheck,
        alert: mdiAlertCircleOutline
      },
      loading: false,
      avatar: '',
      avatarLoading: false,
      form: null,
      showCropperModal: false,
      avatarTimestamp: new Date().getTime(),
      snackbar: {
        show: false,
        message: null
      }
    }
  },

  computed: {
    ...mapState({
      user: state => state.user.userDetails,
      authUser: state => state.auth.user
    }),
    getUserAvatar() {
      return this.avatar
    }
  },

  methods: {
    ...mapActions({
      getUserDetails: 'user/getUserDetails',
      changeAvatar: 'user/changeAvatar',
      updateUser: 'user/updateUser'
    }),

    ...mapMutations({
      clearUserDetails: 'user/clearUserDetails',
      changeTimestamp: 'auth/changeTimestamp'
    }),

    async getUser() {
      this.loading = true
      await this.getUserDetails(this.$route.params.id)
      this.form = new Form(this.user)

      this.avatar = this.user.avatar_thumb_url + '?ts=' + this.avatarTimestamp
      this.loading = false
    },

    async avatarChange(image) {
      this.avatarLoading = true
      let data = new FormData()
      data.append('user_id', this.user.id)
      data.append('avatar', image)
      await this.changeAvatar(data)
      this.avatarTimestamp = new Date().getTime()
      this.touchAuthTimestamp()
      this.avatarLoading = false
      this.loading = true

      setTimeout(() => {
        this.getUser()
        this.showSnackbar('Avatar successfully updated!')
      }, 8000)
    },

    async updateUserDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateUser(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('User details successfully updated!')
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    touchAuthTimestamp() {
      if (this.authUser.id === this.user.id) {
        this.changeTimestamp()
      }
    },

    showSnackbar(message) {
      this.snackbar.message = message
      this.snackbar.show = true
    }
  },

  async created() {
    await this.getUser()
    this.avatar = this.user.avatar_thumb_url
  },

  destroyed() {
    this.clearUserDetails()
  },

  watch: {
    $route() {
      this.getUser()
    }
  }
}
</script>

<style></style>
